import { useState } from 'react';
import useGameStore from '../store/gameStore';
import useCloudStore from '../store/cloudStore';
import type { AIArchetypeChoice, LobbyConfig, SetupPlayer, VictoryCondition } from '../types/gameTypes';
import { LOBBY_COLORS } from '../types/gameTypes';
import { money } from '../lib/format';

const BOT_NAMES = ['Venture Bot', 'Morgan', 'Prudence Bot', 'Vulture Bot', 'Bull Run', 'Bear Cave', 'Connector', 'Tycoon Prime', 'Rainmaker', 'Dealmaker'];

const ARCHETYPES: { value: AIArchetypeChoice; label: string; hint: string }[] = [
  { value: 'AGGRESSIVE', label: '⚔ Aggressive', hint: 'Buys direct, outbids rivals' },
  { value: 'CONSERVATIVE', label: '🛡 Conservative', hint: 'Keeps a $1,000+ cash cushion' },
  { value: 'OPPORTUNISTIC', label: '🎯 Opportunistic', hint: 'Backs districts it already holds' },
  { value: 'RANDOM', label: '🎲 Random', hint: 'Rolled at match start' },
];

const VICTORIES: { value: VictoryCondition; label: string; hint: string }[] = [
  { value: 'NET_WORTH_100K', label: '$100k net worth', hint: 'First tycoon to $100,000 wins' },
  { value: 'NET_WORTH_150K', label: '$150k net worth', hint: 'First tycoon to $150,000 wins' },
  { value: 'LAST_STANDING', label: 'Bankrupt all rivals', hint: 'Last player standing wins' },
  { value: 'ENDLESS', label: 'Endless', hint: 'No victory target — sandbox' },
];

const usedColorsOf = (players: SetupPlayer[]) => new Set(players.map((p) => p.color));
const nextFreeColor = (players: SetupPlayer[]) => LOBBY_COLORS.find((c) => !usedColorsOf(players).has(c)) ?? LOBBY_COLORS[0];

const ControlToggle = ({ slot, updateSlot }: { slot: SetupPlayer; updateSlot: (id: string, patch: Partial<SetupPlayer>) => void }) => (
  <div className="flex overflow-hidden rounded-lg border border-slate-600 text-[11px] font-bold">
    <button
      onClick={() => updateSlot(slot.id, { isAI: false })}
      className={`px-3 py-1.5 ${!slot.isAI ? 'bg-cyan-500/80 text-slate-950' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
    >
      🧑 Human (Hotseat)
    </button>
    <button
      onClick={() => updateSlot(slot.id, { isAI: true })}
      className={`px-3 py-1.5 ${slot.isAI ? 'bg-violet-500/80 text-slate-950' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
    >
      🤖 AI Bot
    </button>
  </div>
);

/** Banner shown when a signed-in player already has cloud saves waiting. */
const CloudSaveBanner = ({ onResume }: { onResume: () => void }) => {
  const { user, saveSlots, pending } = useCloudStore();
  if (!user) return null;
  const resumeTarget = pending ?? (saveSlots.length > 0 ? saveSlots[0] : null);
  if (!resumeTarget) return null;
  const round = 'summary' in resumeTarget ? resumeTarget.summary.round : resumeTarget.market.currentRound;
  const count = pending ? 1 : saveSlots.length;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs">
      <span className="font-semibold text-amber-100">
        ☁️ Máš {count} rozpracovanou hru (Kolo {round}).
      </span>
      <button
        type="button"
        onClick={onResume}
        className="min-h-[34px] rounded-lg border border-amber-400/60 bg-amber-400/90 px-3 py-1 text-[11px] font-black text-slate-950 shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] transition hover:bg-amber-300 active:translate-y-px"
      >
        ▶ Pokračovat v rozehrané hře
      </button>
    </div>
  );
};

/** Pre-game setup lobby: 2–10 player hotseat configuration (humans + bots). */
const LobbyScreen = () => {
  const store = useGameStore();
  const { saveSlots, pending, loadNamedSlot, acceptResume, user } = useCloudStore();
  const [slots, setSlots] = useState<SetupPlayer[]>(() => store.lobby.players.map((p) => ({ ...p })));
  const [startingCash, setStartingCash] = useState<number>(store.lobby.startingCash);
  const [victory, setVictory] = useState<VictoryCondition>(store.lobby.victory);

  const updateSlot = (id: string, patch: Partial<SetupPlayer>) =>
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addPlayer = () => {
    if (slots.length >= 10) return;
    const index = slots.length + 1;
    const usedNames = new Set(slots.map((s) => s.name));
    const botName = BOT_NAMES.find((n) => !usedNames.has(n)) ?? `Bot ${index}`;
    setSlots((prev) => [...prev, { id: `slot-${index}-${Date.now()}`, name: botName, isAI: true, archetype: 'RANDOM', color: nextFreeColor(prev) }]);
  };

  const removePlayer = (id: string) => {
    if (slots.length <= 2) return;
    setSlots((prev) => prev.filter((s) => s.id !== id));
  };

  const start = () => {
    const config: LobbyConfig = {
      players: slots.map((s, i) => ({ ...s, name: s.name.trim() || (s.isAI ? `Bot ${i + 1}` : `Player ${i + 1}`) })),
      startingCash,
      victory,
    };
    store.startGame(config);
  };

  return (
    <div className="flex h-screen w-screen items-start justify-center overflow-y-auto bg-[#121620] p-4 text-slate-100">
      <div className="w-full max-w-3xl space-y-4 py-4">
        <header className="text-center">
          <h1 className="text-2xl font-black tracking-tight">Frakto — Game Setup</h1>
          <p className="mt-1 text-sm text-slate-400">Local hotseat · configure 2 to 10 seats (humans at this PC and/or AI bots).</p>
        </header>
        <CloudSaveBanner
          onResume={() => {
            if (pending) acceptResume();
            else if (user && saveSlots[0]) void loadNamedSlot(saveSlots[0].id);
          }}
        />
        <div className="glass rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Players ({slots.length}/10)</h2>
            <button
              onClick={addPlayer}
              disabled={slots.length >= 10}
              className="rounded-lg bg-emerald-600/90 px-3 py-1.5 text-xs font-extrabold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
            >
              + Add Player
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {slots.map((slot, index) => (
              <div key={slot.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-700/60 bg-slate-900/60 p-2.5">
                <button
                  title="Change color"
                  onClick={() => {
                    const free = LOBBY_COLORS.filter((c) => !usedColorsOf(slots).has(c) || c === slot.color);
                    const next = free[(free.indexOf(slot.color) + 1) % free.length];
                    updateSlot(slot.id, { color: next });
                  }}
                  className="h-8 w-8 shrink-0 rounded-full border-2 border-white/40 shadow"
                  style={{ backgroundColor: slot.color }}
                />
                <span className="w-6 text-center text-[11px] font-bold text-slate-500">{index + 1}</span>
                <input
                  value={slot.name}
                  onChange={(e) => updateSlot(slot.id, { name: e.target.value })}
                  placeholder={`Player ${index + 1}`}
                  className="min-w-[120px] flex-1 rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-1.5 text-sm font-semibold text-slate-100 outline-none focus:border-cyan-400"
                />
                <ControlToggle slot={slot} updateSlot={updateSlot} />
                {slot.isAI && (
                  <select
                    value={slot.archetype}
                    onChange={(e) => updateSlot(slot.id, { archetype: e.target.value as AIArchetypeChoice })}
                    className="rounded-lg border border-slate-600 bg-slate-800 px-2 py-1.5 text-[11px] font-bold text-slate-200"
                  >
                    {ARCHETYPES.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => removePlayer(slot.id)}
                  disabled={slots.length <= 2}
                  className="shrink-0 rounded-lg bg-rose-600/80 px-2.5 py-1.5 text-xs font-extrabold text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
                  title="Remove seat (min 2 players)"
                >
                  ✕ Remove
                </button>
              </div>
            ))}
          </div>
        </div>
        <MatchSettings startingCash={startingCash} setStartingCash={setStartingCash} victory={victory} setVictory={setVictory} slotsCount={slots.length} onStart={start} />
      </div>
    </div>
  );
};

const MatchSettings = ({ startingCash, setStartingCash, victory, setVictory, slotsCount, onStart }: {
  startingCash: number; setStartingCash: (v: number) => void;
  victory: VictoryCondition; setVictory: (v: VictoryCondition) => void;
  slotsCount: number; onStart: () => void;
}) => (
  <>
    <div className="grid gap-4 md:grid-cols-2">
      <div className="glass rounded-2xl p-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Starting capital</h2>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[1500, 2500, 5000].map((cash) => (
            <button
              key={cash}
              onClick={() => setStartingCash(cash)}
              className={`min-h-[52px] rounded-xl border-2 px-2 py-2 text-sm font-extrabold transition ${startingCash === cash ? 'border-emerald-300 bg-emerald-500/20 text-emerald-100' : 'border-slate-700 bg-slate-900/70 text-slate-300 hover:border-slate-500'}`}
            >
              {money(cash)}
            </button>
          ))}
        </div>
      </div>
      <div className="glass rounded-2xl p-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Victory condition</h2>
        <div className="mt-3 grid gap-2">
          {VICTORIES.map((v) => (
            <button
              key={v.value}
              onClick={() => setVictory(v.value)}
              className={`rounded-xl border-2 px-3 py-2 text-left text-xs transition ${victory === v.value ? 'border-amber-300 bg-amber-500/20' : 'border-slate-700 bg-slate-900/70 hover:border-slate-500'}`}
            >
              <span className={`font-extrabold ${victory === v.value ? 'text-amber-100' : 'text-slate-200'}`}>{v.label}</span>
              <span className="block text-[10px] text-slate-400">{v.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
    <button
      onClick={onStart}
      disabled={slotsCount < 2}
      className="min-h-[64px] w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-4 text-lg font-black text-slate-950 shadow-[0_6px_30px_rgba(245,158,11,0.4)] transition hover:from-amber-300 hover:to-orange-400 disabled:cursor-not-allowed disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500"
    >
      🏁 Start Game
    </button>
    <p className="text-center text-[11px] text-slate-500">Pass the device between human turns — bots play on the shared screen.</p>
  </>
);

export default LobbyScreen;
